import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LenderConfiguration } from '../../models/CommsPlatform/lender-configuration';
import { AppService } from '../../app.service';
import { CommsPlatformConfigService } from '../../services/comms-platform-config/comms-platform-config.service';
import { OnboardResult } from '../../models/CommsPlatform/onboard-result';
import { IdName } from '../../models/id-name';
import { ClientLenderConfigurationSearchRequest } from '../../models/CommsPlatform/client-lender-configuration-search-request';
import { ClientLenderConfigurationSearchResult } from '../../models/CommsPlatform/client-lender-configuration-search-result';
import { AddClientLenderConfigurationResult } from '../../models/CommsPlatform/add-client-lender-configuration-result';

@Component({
    selector: 'app-comms-platform-onboarding',
    templateUrl: './comms-platform-onboarding.component.html',
    styleUrl: './comms-platform-onboarding.component.css'
})

export class CommsPlatformOnboardingComponent {

    public clients: IdName[] = [];
    public filteredClients: IdName[] = [];
    public searchResults: ClientLenderConfigurationSearchResult[] = [];
    public isItemEditorVisible: boolean = false;
    public currentLenderConfiguration: LenderConfiguration | null = null;
    public editorSelectedClient: IdName | null = null;
    public editorFilteredClients: IdName[] = [];
    public newLenderConfigurationTemplate: LenderConfiguration = {
        ClientId: 1, 
        LenderName: '',
        TwilioIntegrationId: 0,
        UniqueId: '', 
        AccountSid: 'ACc2a0ceadc55e8bf68fb9ce17fa949783',   // hard-coded (until other options available)
        MessagingServiceSid: '',
        AuthToken: 'e4379fe2d14e50c746ed3e5ad5c54fef',      // hard-coded (until other options available) 
        LenderOutboundWebhookUrl: '',
        LenderInboundWebhookUrl: ''
    };


    @Input() selectedClient: IdName | null = null;
    @Output() selectedClientChange = new EventEmitter<any>();
    @Output() onSelect = new EventEmitter<any>();
    @Input() cssClass: string = '';
    @Input() styleClass: string = '';

    public constructor(public appService: AppService, public commsPlatformConfigService: CommsPlatformConfigService) { }

    public ngOnInit(): void {

        this.currentLenderConfiguration = JSON.parse(JSON.stringify(this.newLenderConfigurationTemplate));

        this.commsPlatformConfigService.getAllClients().subscribe((r: IdName[] | null) => {

            if (r) {

                this.clients = r;
            }
        });
    }

    public editLenderConfiguration = (twilioIntegrationId: number = 0): void => {

        if (twilioIntegrationId == 0) { // setup for new configuration 

            this.currentLenderConfiguration = JSON.parse(JSON.stringify(this.newLenderConfigurationTemplate)); // deep-clone the template object

            if (this.selectedClient) {

                let searchedClient = this.clients.find(c => c.Id === this.selectedClient!.Id);

                if (searchedClient) {

                    this.editorSelectedClient = searchedClient;
                }
            }

        } else { // existing

            var searchResult = this.searchResults.filter(i => i.TwilioIntegrationId == twilioIntegrationId)[0];

            if (searchResult) { // we know that the data is available in the search results but this should really come from the api 

                this.currentLenderConfiguration = new LenderConfiguration();

                this.currentLenderConfiguration.ClientId = searchResult.ClientId;
                this.currentLenderConfiguration.LenderName = searchResult.LenderName;
                this.currentLenderConfiguration.TwilioIntegrationId = searchResult.TwilioIntegrationId;
                this.currentLenderConfiguration.UniqueId = searchResult.UniqueId;
                this.currentLenderConfiguration.AccountSid = searchResult.AccountSid;
                this.currentLenderConfiguration.MessagingServiceSid = searchResult.MessagingServiceSid;
                this.currentLenderConfiguration.AuthToken = searchResult.AuthToken;
                this.currentLenderConfiguration.LenderOutboundWebhookUrl = searchResult.LenderOutboundWebhookUrl ?? "";
                this.currentLenderConfiguration.LenderInboundWebhookUrl = searchResult.LenderInboundWebhookUrl ?? "";
                this.editorSelectedClient = this.clients.find(c => c.Id === searchResult.ClientId) ?? null;
            }
        }

        this.isItemEditorVisible = true;
    }

    public saveLenderConfiguration = (): void => {

        const missing = !this.editorSelectedClient!.Id || 
                        !this.currentLenderConfiguration!.LenderName ||
                        !this.currentLenderConfiguration!.UniqueId ||
                        !this.currentLenderConfiguration!.AccountSid ||
                        !this.currentLenderConfiguration!.MessagingServiceSid ||
                        !this.currentLenderConfiguration!.AuthToken ||
                        !this.currentLenderConfiguration!.LenderOutboundWebhookUrl ||
                        !this.currentLenderConfiguration!.LenderInboundWebhookUrl;

        if (missing) {

            this.appService.showCommonMessageDialog("Validation Alert", "Please fill out all fields before saving");

        } else {

            if (this.currentLenderConfiguration!.TwilioIntegrationId > 0) {

                this.appService.showCommonMessageDialog("Unsupported Operation", "Editing existing configurations not supported.  This is read-only view.  The 'Save' feature currently supports the creation of new configurations only");

            } else {

                this.currentLenderConfiguration!.ClientId = this.editorSelectedClient!.Id!;

                this.commsPlatformConfigService.onBoardCommsPlatformLender(this.currentLenderConfiguration!).subscribe((r: AddClientLenderConfigurationResult | null) => {

                    if (r) {

                        if (r.FailureReason) {

                            this.appService.showCommonMessageDialog("Error During Save", r.FailureReason);

                        } else {

                            this.searchConfigurations();
                            this.isItemEditorVisible = false

                            this.appService.showCommonMessageDialog("New Configuration Added", "New Lender Configuration Succeeded !");
                        }
                    }
                });
            }
        }
    }

    public filterClients(event: any) {

        let filteredItems: IdName[] = [];
        let query = event.query;

        for (let i = 0; i < (this.clients as IdName[]).length; i++) {

            let item = (this.clients as IdName[])[i];

            if (item.Name.toLowerCase().indexOf(query.toLowerCase()) !== -1 || item.Id.toString().indexOf(query) !== -1) {

                filteredItems.push(item);
            }
        }

        this.filteredClients = filteredItems;
    }

    public onSelectClient(event: any) {

        this.selectedClientChange.emit(this.selectedClient!);
        this.onSelect.emit(this.selectedClient);
    }

    public onKeyUp(event: any) {

        if (!event.currentTarget.value) {

            this.selectedClientChange.emit("");
            this.onSelect.emit("");
        }
    }

    public searchConfigurations = (): void => {

        let searchRequest: ClientLenderConfigurationSearchRequest = new ClientLenderConfigurationSearchRequest();

        searchRequest.ClientId = this.selectedClient ? this.selectedClient!.Id : 0;

        this.commsPlatformConfigService.searchCommsPlatformConfigurations(searchRequest).subscribe((r: ClientLenderConfigurationSearchResult[] | null) => {

            if (r) {

                this.searchResults = r;
            }
        });
    }

    public filterEditorClients(event: any) {

        let filteredItems: IdName[] = [];
        let query = event.query;

        for (let i = 0; i < this.clients.length; i++) {

            let item = this.clients[i];

            if (item.Name.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||

                item.Id.toString().indexOf(query) !== -1) {

                filteredItems.push(item);
            }
        }

        this.editorFilteredClients = filteredItems;
    }

    public onEditorSelectClient(event: any) {

        if (this.editorSelectedClient && this.currentLenderConfiguration) {

            this.currentLenderConfiguration.ClientId = this.editorSelectedClient.Id;
        }
    }
}
